import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import HomeSection from '../HomeSection';
import BandEventRow from '../../../molecules/BandEventRow';
import BandEventActionsMenu from '../../../molecules/BandEventActionsMenu';
import EditorialEmptyState from '../../../molecules/EditorialEmptyState';
import BandSectionActionButton from '../../../molecules/EditorialSectionActionButton';
import './index.scss';

const ActionButton = ({ action }) => (
  <BandSectionActionButton
    label={action?.label}
    onClick={action?.onClick}
    variant={action?.variant}
  />
);

ActionButton.propTypes = {
  action: PropTypes.shape({
    label: PropTypes.node,
    onClick: PropTypes.func,
    variant: PropTypes.oneOf(['accent', 'ghost']),
  }),
};

export default function HomeEventsSection({
  id = 'schedule',
  eyebrow,
  title,
  side,
  events = [],
  onCreateEvent,
  onEditEvent,
  onDeleteEvent,
  canManageEvents = false,
  createEventLabel,
  emptyTitle,
  emptyDescription,
}) {
  const { t } = useTranslation();
  const createAction = onCreateEvent ? {
    label: createEventLabel || t('bandPage.schedule.createEvent', { defaultValue: 'Create event' }),
    onClick: onCreateEvent,
    variant: 'accent',
  } : null;
  const canShowEventActions = Boolean(canManageEvents && (onEditEvent || onDeleteEvent));

  const renderEventAction = (event) => {
    const existingAction = event.action ? <ActionButton action={event.action} /> : null;
    const sourceEvent = event.sourceEvent || null;
    const canManageSourceEvent = canShowEventActions && sourceEvent?.canManage !== false;
    const menu = canManageSourceEvent && sourceEvent ? (
      <BandEventActionsMenu
        event={sourceEvent}
        onEdit={onEditEvent}
        onDelete={onDeleteEvent}
      />
    ) : null;

    if (!existingAction && !menu) {
      return null;
    }

    return (
      <div className="band-schedule__row-actions">
        {existingAction}
        {menu}
      </div>
    );
  };

  return (
    <HomeSection
      id={id}
      eyebrow={eyebrow || t('bandPage.schedule.eyebrow')}
      title={title || t('bandPage.schedule.title')}
      side={side || (createAction ? <ActionButton action={createAction} /> : null)}
      className="band-schedule"
    >
      {events.length > 0 ? (
        <div className="band-schedule__list">
          {events.map((event, idx) => (
            <BandEventRow
              key={event.id || idx}
              day={event.day}
              month={event.month}
              title={event.title}
              subtitle={event.subtitle}
              venue={event.venue}
              location={event.location}
              description={event.description}
              pill={event.pill}
              pillStatus={event.pillStatus}
              action={renderEventAction(event)}
            />
          ))}
        </div>
      ) : (
        <EditorialEmptyState
          title={emptyTitle || t('bandPage.schedule.emptyTitle', { defaultValue: 'No events yet' })}
          description={emptyDescription || t('bandPage.schedule.emptyDescription', { defaultValue: 'Upcoming shows scheduled by the band will appear here.' })}
        />
      )}
    </HomeSection>
  );
}

HomeEventsSection.propTypes = {
  id: PropTypes.string,
  eyebrow: PropTypes.node,
  title: PropTypes.node,
  side: PropTypes.node,
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sourceEvent: PropTypes.object,
    day: PropTypes.node,
    month: PropTypes.node,
    title: PropTypes.node,
    subtitle: PropTypes.node,
    venue: PropTypes.node,
    location: PropTypes.node,
    description: PropTypes.node,
    pill: PropTypes.node,
    pillStatus: PropTypes.oneOf(['sold', 'few', 'default']),
    action: PropTypes.shape({
      label: PropTypes.node,
      onClick: PropTypes.func,
      variant: PropTypes.oneOf(['accent', 'ghost']),
    }),
  })),
  onCreateEvent: PropTypes.func,
  onEditEvent: PropTypes.func,
  onDeleteEvent: PropTypes.func,
  canManageEvents: PropTypes.bool,
  createEventLabel: PropTypes.node,
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
};
